import { Select, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import QueryString from 'qs';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import { getLandingDatabases } from '../../../api/landingDatabase.api';

import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Pagination';
import SelectCampus from '../../../components/SelectCampus';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import config from '../../../lib/config';
import { RootState } from '../../../store/rootReducer';
import { ISelect } from '../../../models/common.model';
import {
  IGetLandingDatabases,
  ILandingDatabaseItem,
} from '../../../models/landingDatabase.model';
import { getAxiosBaseUrl } from '../../../lib/util';
const { Option } = Select;

const LandingDatabase = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });

  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { serverDate } = useSelector((state: RootState) => state.serverDate);

  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetLandingDatabases>();

  const [keyword, setKeyword] = useState<string>('');
  const [page, setPage] = useState(1);
  const [param, setParam] = useState({});
  const [month, setMonth] = useState<any>(null);
  const [monthList, setMonthList] = useState<ISelect[]>([]);
  const [year, setYear] = useState<any>(null);
  const [yearList, setYearList] = useState<ISelect[]>([]);

  const landingDatabaseUrl = '/landing/db';

  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  const SY0005 = commonCodes.find(
    (item) => item.commonCode === 'SY0005',
  )?.detailCode; // 연정보
  const SY0009 = commonCodes.find(
    (item) => item.commonCode === 'SY0009',
  )?.detailCode; // 월정보

  useEffect(() => {
    if (currentCampus) {
      const _param: any = {
        pageNo: page,
        perPage: config.pageSize,
      };

      _param.campusCode = String(currentCampus);

      if (query.page) {
        _param.pageNo = Number(query.page);
        setPage(Number(query.page));
      }

      if (query.title) {
        _param.title = String(query.title);
        setKeyword(String(query.title));
      }

      if (query.inquiryYear) {
        _param.inquiryYear = String(query.inquiryYear);
        setYear(String(query.inquiryYear));
      }

      if (query.inquiryMonth) {
        _param.inquiryMonth = String(query.inquiryMonth);
        setMonth(String(query.inquiryMonth));
      }

      if (serverDate) {
        execute(getLandingDatabases(_param));
        _param.page = _param.pageNo;
        delete _param.pageNo;
        delete _param.perPage;
        setParam(_param);
      }
    }
  }, [currentCampus, location, serverDate]);

  useEffect(() => {
    // 연도 설정
    if (SY0005) {
      const _yearList: any = [];
      const _SY0005 = [...SY0005].sort((a, b) =>
        a.orderSeq < b.orderSeq ? -1 : a.orderSeq > b.orderSeq ? 1 : 0,
      );
      _SY0005.forEach((item: any) => {
        _yearList.push({
          label: item.korName,
          value: item.detailCode,
        });
      });
      setYearList(_yearList);
    }

    // 월 성정
    if (SY0009) {
      const _monthList: any = [];
      const _SY0009 = [...SY0009].sort((a, b) =>
        a.orderSeq < b.orderSeq ? -1 : a.orderSeq > b.orderSeq ? 1 : 0,
      );
      _SY0009.forEach((item: any) => {
        _monthList.push({
          label: item.korName,
          value: item.detailCode,
        });
      });
      setMonthList(_monthList);
    }
  }, [commonCodes]);

  const onDownloadExcel = () => {
    const mgmtLandingDatabaseUrl = `${getAxiosBaseUrl()}/mgmt/landing`;
    const param: any = {};
    param.campusCode = currentCampus;
    param.title = keyword;
    param.inquiryYear = year;
    param.inquiryMonth = month;

    const url = `${mgmtLandingDatabaseUrl}/download/database?${QueryString.stringify(
      param,
    )}`;
    window.open(url, '_blank');
  };

  const onSearch = () => {
    const param: any = { page: 1 };
    param.campusCode = currentCampus;
    param.title = keyword;
    param.inquiryYear = year;
    param.inquiryMonth = month;

    history.push(`${landingDatabaseUrl}?${QueryString.stringify(param)}`);
  };

  const columns: TableColumnsType<ILandingDatabaseItem> = [
    {
      title: t('landing.db.column1'),
      dataIndex: 'landingDatabaseCode',
      key: 'landingDatabaseCode',
      align: 'center',
      sorter: (a, b) => a.landingDatabaseCode - b.landingDatabaseCode,
      render: (value, record) => {
        return value;
      },
    },
    {
      title: t('landing.db.column2'),
      dataIndex: 'landingTitle',
      key: 'landingTitle',
      align: 'center',
      sorter: (a, b) => a.landingTitle?.localeCompare(b.landingTitle),
      render: (value, record) => {
        return value;
      },
    },
    {
      title: t('landing.db.column3'),
      dataIndex: 'inquiryDateTime',
      key: 'inquiryDateTime',
      align: 'center',
      sorter: (a, b) => a.inquiryDateTime?.localeCompare(b.inquiryDateTime),
      render: (value) => moment(value).format('YYYY.MM.DD HH:mm:ss'),
    },
    {
      title: t('landing.db.column4'),
      dataIndex: 'campusName',
      key: 'campusName',
      align: 'center',
      sorter: (a, b) => a.campusName?.localeCompare(b.campusName),
      render: (value, record) => {
        return value;
      },
    },
    {
      title: t('landing.db.column5'),
      dataIndex: 'parentName',
      key: 'parentName',
      align: 'center',
      sorter: (a, b) => a.parentName?.localeCompare(b.parentName),
      render: (value, record) => {
        return value;
      },
    },
    {
      title: t('landing.db.column6'),
      dataIndex: 'parentPhoneNumber',
      key: 'parentPhoneNumbers',
      align: 'center',
      sorter: (a, b) => a.parentPhoneNumber?.localeCompare(b.parentPhoneNumber),
      render: (value, record) => {
        if (value && value.length < 10) {
          return value;
        } else if (value && value.length === 10) {
          return (
            value.substring(0, 3) +
            '-' +
            value.substring(3, 6) +
            '-' +
            value.substring(6, 10)
          );
        } else {
          return (
            value.substring(0, 3) +
            '-' +
            value.substring(3, 7) +
            '-' +
            value.substring(7, 11)
          );
        }
      },
    },
    {
      title: t('landing.db.column7'),
      dataIndex: 'schoolName',
      key: 'schoolNames',
      align: 'center',
      sorter: (a, b) => a.schoolName?.localeCompare(b.schoolName),
      render: (value, record) => {
        return value;
      },
    },
    {
      title: t('landing.db.column8'),
      dataIndex: 'grade',
      key: 'grade',
      align: 'center',
      sorter: (a, b) => a.grade?.localeCompare(b.grade),
      render: (value, record) => {
        return value;
      },
    },
    {
      title: t('landing.db.column9'),
      dataIndex: 'inflow',
      key: 'inflow',
      align: 'center',
      sorter: (a, b) => a.inflow?.localeCompare(b.inflow),
      render: (value, record) => {
        return value;
      },
    },
    {
      title: '홈페이지 가입',
      dataIndex: 'homepageJoin',
      key: 'homepageJoin',
      align: 'center',
      sorter: (a, b) => a.homepageJoin?.localeCompare(b.homepageJoin),
      render: (value, record) => {
        return value;
      },
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-homepage-big.svg"
      parentMenu={t('homepage.landing.title1')}
      currentMenu={t('homepage.landing.title3')}
      onlyTitle={true}
    />	  
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label>
            <div>
              <SelectCampus />
            </div>
          </li>
          <li>
            <label>{t('common.text.year')}</label>
            <div>
              <Select
                placeholder={t('common.text.year')}
                value={year}
                onChange={(value) => setYear(value)}
              >
                <Option value="all">{t('common.text.all')}</Option>
                {yearList?.map((item: any) => {
                  return (
                    <Option key={item.value} value={item.value}>
                      {item.label}
                    </Option>
                  );
                })}
              </Select>
            </div>
          </li>
          <li>
            <label>{t('landing.imagemanage.column17')}</label>
            <div>
              <Select
                placeholder={t('landing.imagemanage.column17')}
                value={month}
                onChange={(value) => setMonth(value)}
              >
                <Option value="all">{t('common.text.all')}</Option>
                {monthList?.map((item: any) => {
                  return (
                    <Option key={item.value} value={item.value}>
                      {item.label}
                    </Option>
                  );
                })}
              </Select>
            </div>
          </li>
          <li>
            <input
              type="text"
              className="form-control w-240p"
              placeholder={t('landing.db.placeholder1')}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">
            {t('common.text.count')}: <b>{res?.total}</b>
            {t('common.text.unit')}
          </div>
          <div className="col-6 text-right" />
        </div>
        <div className="comm-table__area">
          <Table
            columns={columns}
            dataSource={res?.data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            rowClassName="pointer"
            rowKey="landingDatabaseCode"
          />
        </div>
        <div className="comm-bx__between">
          <div className="comm-btn__area">
            <Button
                className="btn-basic green"
                onClick={() => onDownloadExcel()}
                text="Download"
                disabled={!res?.data.length || res?.data.length === 0}
                writeAuthYn={writeAuthYn}
                useExcelIcon
              />
          </div>
          <div className="common-paging center">
            <Pagination
              count={res?.total || 0}
              page={page}
              param={param}
              path={landingDatabaseUrl}
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default LandingDatabase;
